'use client';
import * as React from 'react';
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Link as ReactRouterLink,
} from 'react-router';

import { LinkProps } from '../shared/Link';
import {
  Router,
  Navigate,
  AppProvider,
  type AppProviderProps,
} from '../AppProvider/AppProvider';

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { href, history, ...rest } = props;
  return (
    <ReactRouterLink
      ref={ref}
      to={href}
      replace={history === 'replace'}
      {...rest}
    />
  );
});

function ReactRouterAppProvider(props: AppProviderProps) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const navigateImpl = React.useCallback<Navigate>(
    (url, { history = 'auto' } = {}) => {
      if (history === 'auto' || history === 'push') {
        return navigate(url);
      }
      if (history === 'replace') {
        return navigate(url, { replace: true });
      }
      throw new Error(`Invalid history option: ${history}`);
    },
    [navigate]
  );

  const routerImpl = React.useMemo<Router>(
    () => ({
      Link,
      pathname,
      searchParams,
      navigate: navigateImpl,
    }),
    [pathname, searchParams, navigateImpl]
  );

  return <AppProvider router={routerImpl} {...props} />;
}

export { ReactRouterAppProvider };
